import {
  _30_MIN_INDIVIDUAL_90832,
  _45_MIN_INDIVIDUAL_90834,
  _60_MIN_INDIVIDUAL_90837,
  DIAGNOSTIC_INTERVIEW_90791,
  DIAGNOSTIC_INTERVIEW_90792,
  ESTABLISHED_PATIENT_OFFICE_OR_OTHER_OUTPATIENT_SERVICES_99215,
  FAMILY_THERAPY_90847,
  MEDICATION_MGMT_99212,
  MEDICATION_MGMT_99213,
  MEDICATION_MGMT_99214,
  FAMILY_THERAPY_WO_PATIENT_90846,
  OFFICE_OR_OTHER_OUTPATIENT_VISIT_W_NEW_PATIENT_99205,
  OFFICE_OR_OTHER_OUTPATIENT_VISIT_W_NEW_PATIENT_99204,
} from "@/components/Provider/ScheduleAvailability/constants";

import { BehavioralExploration, BehavioralSymptomTrends } from "./BehavioralWorkflowSteps";
import { BehavioralReviewLastNotes, PrescribeReviewLastNotes } from "./ReviewLastNotes";
import { DisplayWorkflowStepWithVideoClient } from "./DisplayWorkflowStepWithVideoClient";
import { enrichBehavioralNoteContent, enrichPrescribeNoteContent } from "./WorkflowFunctions";
import { InterventionAndPlan } from "./InterventionAndPlan/InterventionAndPlan";
import {
  PrescribeMedicalInformation,
  PrescribeMedicationImpact,
  PrescribeAssessmentPlan,
  PrescribeSymptomTrends,
} from "./PrescribeWorkflowSteps";
import { providerTypeToDefaultEvent } from "./helpers";
import { ScheduleNextCall } from "./ScheduleNextCall";
import { Signoff } from "./Signoff";

/*
    This file is used to give a high level overview of the guided session functionality
    to orient yourself with all the files/components

    VideoClient src/containers/VideoClient/index.js
        this component sets up global functionality specific to the video call
        such as audio/video, and the workflow state based on the call event_type/cpt
        code

        GuidedVideoCall src/components/Common/GuidedVideoCall/index.js
            responsible for displaying the high level steps needed for
            the video call. It shows the following flow to a provider
                1. AllowAVStep src/components/Common/GuidedVideoCall/Steps/AllowAVStep.js
                2. PreviewStep src/components/Common/GuidedVideoCall/Steps/PreviewStep.js
                3. ProviderVideoWorkflowStep src/components/Common/GuidedVideoCall/Steps/ProviderVideoWorkflowStep.js
                    (called ProviderVideoWorkflow in the GuidedVideoCall file)

            After the third step has been reached the workflow specific steps relevant to
            the call's cpt code/event type take effect, and all of the steps are rendered inside
            the ProviderVideoWorkflowStep component

            NOTE: ProviderVideoWorkflowStep maintains state
            separate from VideoClient and GuidedVideoCall such as its own step counter
            that marks what workflow step the provider is on
 */

/*
   The schema for a workflow is defined below. The specific steps and details are broken
   out into their own associated functions based on event type/provider type

    {
        "steps": {
            header: string (optional)
                the string that is displayed in VideoViewBaseComponent
                if omitted the header is not displayed,
            caption: string (optional)
                the string that is displayed in the GuidedVideoSection within
                the DisplayWorkflowStepWithVideoClient src/components/Common/GuidedVideoCall/Steps/ProviderWorkflow/DisplayWorkflowStepWithVideoClient.js)
            short: string (required)
                the string that is displayed in the GuidedVideoSection within the GuidedCallProgress
                component,
            shortClassName: string (optional)
                allows modifications to the workflow step arrows at the top of the guided session
            component: react component (required)
                the component that is formed within VideoViewBaseComponent src/components/Common/GuidedVideoCall/index.js
                typically this will be DisplayWorkflowStepWithVideoClient,
            subComponent: react component (optional depending on the component used)
                typically the child component of component,
            startCall: bool (optional)
                runs code specific to starting the call when the workflow step is incremented
                see ProviderVideoWorkflowStep for how its used,
            carePlanTips: str[]
                Used to display tips on how the care plan
                should be filled out see the CarePlanWidget src/components/Common/GuidedVideoCall/Steps/ProviderWorkflow/CarePlanWidget/CarePlanWidget.js,
            saveNote: bool (optional)
                runs code specific to saving the note when the workflow step is incremented
                see ProviderVideoWorkflowStep for how its used,
            customTransition: bool (optional)
                when true hides the next/prev buttons on the guided session
                when false we assume that the component/subComponent will add the next/prev steps
                see DisplayWorkflowStepWithVideoClient for how its used,
            endCall: bool (optional)
                runs code specific to ending the call when the workflow step is incremented
                see ProviderVideoWorkflowStep for how its used,
            isDisplayingGuidedSession: bool (optional)
                a bandaid to adjust the guided session height to account for the tool bar offset
                when the video client is shown see src/components/Common/GuidedVideoCall/index.js
        },
        "details": {

        }
    }

    */
export const BEHAVIORAL_NOTE_V2_WORKFLOW_STEPS = () => [
  {
    component: BehavioralReviewLastNotes,
    header: "Review Previous Session Summary",
    short: "Review",
  },
  {
    caption: "Symptom Trends",
    component: DisplayWorkflowStepWithVideoClient,
    isDisplayingGuidedSession: true,
    short: "Symptoms",
    startCall: true,
    subComponent: BehavioralSymptomTrends,
    subTitle:
      "Document the patient’s current symptoms, including severity, duration, intensity, symptom trend, and impact on functioning.",
  },
  {
    caption: "Exploration",
    component: DisplayWorkflowStepWithVideoClient,
    isDisplayingGuidedSession: true,
    saveNote: true,
    short: "Exploration",
    subComponent: BehavioralExploration,
    subTitle:
      "Document any changes (e.g., home, work, relationships, healthcare use, etc.) correlating/affecting symptoms.",
  },
  {
    caption: "Interventions & Plans",
    component: DisplayWorkflowStepWithVideoClient,
    customTransition: true,
    isDisplayingGuidedSession: true,
    saveNote: true,
    short: "Interventions/Plans",
    shortClassName: "interventions_and_plans",
    subComponent: InterventionAndPlan,
    subTitle:
      "Document therapeutic techniques aligned with evidence-based practices used during the session and strategies recommended between sessions.",
  },
  {
    caption: "Schedule Next Appointment",
    component: DisplayWorkflowStepWithVideoClient,
    customTransition: true,
    isDisplayingGuidedSession: true,
    saveNote: true,
    short: "Schedule",
    subComponent: ScheduleNextCall,
  },
  {
    component: Signoff,
    endCall: true,
    header: "Review Session Summary",
    saveNote: true,
    short: "Sign-Off",
  },
];

export const BEHAVIORAL_NOTE_V2_WORKFLOW_DETAILS = () => {
  return {
    createNote: true,
    endCallShouldRoute: false,
    iconComponents: [],
    noteEnricher: enrichBehavioralNoteContent,
    noteVersion: 2,
    relevantPatientQuestionSections: {
      changes: [1],
      healthCareUse: [2],
      symptomTrends: [0, 3, 4],
    },
    startingStep: -1,
    viewLastNoteOther: "PRESCRIBE",
    viewLastNoteSelf: true,
  };
};

export const BEHAVIORAL_CLINICAL_ASSESSMENT_WORKFLOW_DETAILS = () => {
  return {
    createClinicalAssessmentNote: true,
    createNote: false,
    endCallShouldRoute: true,
    iconComponents: [],
    showNoShowModalOnly: true,
    skipEndCallModal: false,
    startingStep: 0,
    viewLastNoteOther: null,
    viewLastNoteSelf: false,
  };
};

export const PRESCRIBE_NOTE_V2_WORKFLOW_STEPS = () => [
  {
    component: PrescribeReviewLastNotes,
    header: "Review Previous Session Summary",
    short: "Review",
  },
  {
    caption: "Symptom Trends",
    carePlanTips: [
      "Explore overall progress",
      "Note symptoms/frequency/duration/intensity",
      "Identify triggers or precipitants impacting symptoms",
      "Explore patient feedback",
      " Complete prescriber rating of symptom duration",
    ],
    component: DisplayWorkflowStepWithVideoClient,
    short: "Symptoms",
    startCall: true,
    subComponent: PrescribeSymptomTrends,
  },
  {
    caption: "Medication Impact",
    carePlanTips: [
      "Review current impact of medications",
      "Review patient report re: medication experiences",
      "Utilize I-STOP if clinically warranted",
      "Complete prescriber rating regarding benefit and side effects",
    ],
    component: DisplayWorkflowStepWithVideoClient,
    saveNote: true,
    short: "Medication",
    subComponent: PrescribeMedicationImpact,
  },
  {
    caption: "Medical Information",
    carePlanTips: [
      "Note Psychosocial changes",
      "Explore healthcare use",
      "Review labs as indicated",
      "Complete mental status ",
    ],
    component: DisplayWorkflowStepWithVideoClient,
    saveNote: true,
    short: "Medical",
    subComponent: PrescribeMedicalInformation,
  },
  {
    caption: "Assessment & Plan",
    carePlanTips: [
      "Rate patient progress across each diagnosis",
      "Complete assessment and plan",
      "Enter medication ( medication initiated or adjusted, dosage & change)",
      "Address medication adherence and check adherence interventions",
    ],
    component: DisplayWorkflowStepWithVideoClient,
    saveNote: true,
    short: "Assessment",
    subComponent: PrescribeAssessmentPlan,
  },
  {
    caption: "Schedule Next Appointment",
    carePlanTips: ["Try to maintain regular appointment times ", "Confirm next appointment"],
    component: DisplayWorkflowStepWithVideoClient,
    customTransition: true,
    saveNote: true,
    short: "Schedule",
    subComponent: ScheduleNextCall,
  },
  {
    component: Signoff,
    header: "Review Previous Session Summary",
    short: "Review",
  },
];

export const PRESCRIBE_NOTE_V2_WORKFLOW_DETAILS = () => {
  return {
    createNote: true,
    endCallShouldRoute: false,
    iconComponents: [],
    noteEnricher: enrichPrescribeNoteContent,
    noteVersion: 2,
    relevantPatientQuestionSections: {
      medicalProblems: [6],
      medicationDifficulties: [5],
      medicationTrends: [1, 2, 3, 4],
      symptomTrends: [0],
    },
    startingStep: -1,
    viewLastNoteOther: "BEHAVIORAL",
    viewLastNoteSelf: true,
  };
};

export const DEFAULT_WORKFLOW_STEPS = () => [
  {
    caption: "Video Consultation",
    component: DisplayWorkflowStepWithVideoClient,
    fullScreen: true,
    short: "Consultation",
    startCall: true,
  },
];

export const DEFAULT_WORKFLOW_DETAILS = () => {
  return {
    createNote: true,
    endCallShouldRoute: true,
    iconComponents: [],
    noteVersion: 1,
    skipEndCallModal: true,
    startingStep: 0,
    viewLastNoteOther: null,
    viewLastNoteSelf: false,
  };
};

export const getCPTWorkflow = (eventType, provider) => {
  let processedEventType = eventType;

  if (!processedEventType) {
    processedEventType = providerTypeToDefaultEvent(provider);
  }

  /* oxlint-disable */
  switch (processedEventType) {
    case _30_MIN_INDIVIDUAL_90832:
    case _45_MIN_INDIVIDUAL_90834:
    case _60_MIN_INDIVIDUAL_90837:
    case OFFICE_OR_OTHER_OUTPATIENT_VISIT_W_NEW_PATIENT_99204:
    case OFFICE_OR_OTHER_OUTPATIENT_VISIT_W_NEW_PATIENT_99205:
    case FAMILY_THERAPY_WO_PATIENT_90846:
    case FAMILY_THERAPY_90847: {
      return {
        details: BEHAVIORAL_NOTE_V2_WORKFLOW_DETAILS(),
        steps: BEHAVIORAL_NOTE_V2_WORKFLOW_STEPS(),
      };
    }

    case DIAGNOSTIC_INTERVIEW_90791: {
      return {
        details: BEHAVIORAL_CLINICAL_ASSESSMENT_WORKFLOW_DETAILS(),
        steps: DEFAULT_WORKFLOW_STEPS(),
      };
    }

    case DIAGNOSTIC_INTERVIEW_90792:
    case MEDICATION_MGMT_99212:
    case MEDICATION_MGMT_99213:
    case MEDICATION_MGMT_99214:
    case ESTABLISHED_PATIENT_OFFICE_OR_OTHER_OUTPATIENT_SERVICES_99215: {
      return {
        details: PRESCRIBE_NOTE_V2_WORKFLOW_DETAILS(),
        steps: PRESCRIBE_NOTE_V2_WORKFLOW_STEPS(),
      };
    }

    default: {
      return {
        details: DEFAULT_WORKFLOW_DETAILS(),
        steps: DEFAULT_WORKFLOW_STEPS(),
      };
    }
  }
  /* oxlint-enable */
};
